import { DEFAULT_CONFIG, CANVAS_GEOMETRY } from '../../../utils/constants';
import { timeToX, getCycleBounds } from '../../../utils/coordinate-math';
import { calculateScale, getMajorTicks, getMinorTicks } from '../../../utils/time-scale-calculations';

import { timelineTheme, OUT_OF_RANGE_CYCLE_COLORS } from '../../theme';
import { CycleData, TimelineTheme, OverlapColors } from '../../../types';
import { ICanvasContext } from './ICanvasContext';

/**
 * Returns the time needle SVG as a string for canvas rendering.
 */
function getTimeNeedleIconString(color: string): string {
	return `<svg width="10" height="51" viewBox="0 0 10 40" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M6 12L6 51H4L4 12H6Z" fill="${color}"/>
		<path d="M9.68195 8.72291L5 16L0.318045 8.72291C0.110406 8.40017 0 8.02452 0 7.64076V2C0 0.895431 0.89543 0 2 0H8C9.10457 0 10 0.895429 10 2V7.64076C10 8.02452 9.88959 8.40018 9.68195 8.72291Z" fill="${color}"/>
	</svg>`;
}

const needleImageCache: Map<string, HTMLImageElement> = new Map();
const pendingCallbacks: Set<() => void> = new Set();

const OUT_OF_RANGE_DASH = [4, 4];

/**
 * Class responsible for rendering graphical elements on the timeline canvas.
 */
export class CanvasDrawer {
	private ctx: ICanvasContext;

	private width: number;

	private height: number;

	private videoDuration: number;

	private overlapColors?: OverlapColors;

	/**
	 * @param ctx - The 2D rendering context for the canvas.
	 * @param width - The width of the canvas in pixels.
	 * @param height - The height of the canvas in pixels.
	 * @param videoDuration - The total duration of the video in seconds.
	 * @param theme - Optional custom theme.
	 * @param margin - Optional custom margin (uses DEFAULT_CONFIG.MARGIN if not provided).
	 * @param overlapColors - Optional colors for overlapping cycles.
	 */
	constructor(
		ctx: ICanvasContext,
		width: number,
		height: number,
		videoDuration: number,
		private theme: TimelineTheme = timelineTheme,
		private margin: number = DEFAULT_CONFIG.MARGIN,
		private onImageLoad?: () => void,
		overlapColors?: OverlapColors
	) {
		this.ctx = ctx;
		this.width = width;
		this.height = height;
		this.videoDuration = videoDuration;
		this.overlapColors = overlapColors;
	}

	/**
	 * Sets the overlap colors for rendering cycles with overlaps.
	 * @param colors - Record mapping overlap count to color string.
	 */
	public setOverlapColors(colors: OverlapColors | undefined) {
		this.overlapColors = colors;
	}

	/**
	 * Type guard to check if the context supports drawImage.
	 */
	private isCanvas2DContext(ctx: ICanvasContext): ctx is CanvasRenderingContext2D {
		return 'drawImage' in ctx;
	}

	/**
	 * Clears the canvas and fills it with the background color.
	 */
	public clear() {
		this.ctx.clearRect(CANVAS_GEOMETRY.ZERO_COORDINATE, CANVAS_GEOMETRY.ZERO_COORDINATE, this.width, this.height);
		this.ctx.fillStyle = this.theme.colors.background;
		this.ctx.fillRect(CANVAS_GEOMETRY.ZERO_COORDINATE, CANVAS_GEOMETRY.ZERO_COORDINATE, this.width, this.height);
	}

	/**
	 * Draws the time markings (ruler) at the bottom of the canvas.
	 * It automatically calculates intervals based on the video duration and available width.
	 */
	public drawRuler() {
		if (this.videoDuration === CANVAS_GEOMETRY.ZERO_COORDINATE) return;

		const scale = calculateScale(this.videoDuration, undefined, this.width);
		const { majorInterval, minorInterval, majorHeight, minorHeight } = scale;

		this.ctx.strokeStyle = this.theme.colors.ruler;
		this.ctx.lineWidth = this.theme.dimensions.lineWidth;

		const majorTicks = getMajorTicks(this.videoDuration, majorInterval);
		majorTicks.forEach((time) => {
			const x = timeToX(time, this.videoDuration, this.width, this.margin);
			this.drawPin(x, majorHeight);
		});

		const minorTicks = getMinorTicks(this.videoDuration, majorInterval, minorInterval);
		minorTicks.forEach((time) => {
			const x = timeToX(time, this.videoDuration, this.width, this.margin);
			this.drawPin(x, minorHeight);
		});
	}

	/**
	 * Draws an individual vertical tick for the ruler.
	 * @param x - The horizontal position of the tick.
	 * @param height - The height of the tick in pixels.
	 */
	private drawPin(x: number, height: number) {
		this.ctx.beginPath();
		this.ctx.moveTo(x, this.height - height);
		this.ctx.lineTo(x, this.height);
		this.ctx.stroke();
	}

	/**
	 * Draws an identified cycle with an orange style.
	 * @param cycle - The data of the cycle to be drawn.
	 * @param isHovered - Whether the mouse is hovering over this cycle.
	 * @param customColor - Optional custom background color for the cycle.
	 */
	public drawIdentifiedCycle(cycle: CycleData, isHovered = false, customColor?: string) {
		const { startX, endX } = getCycleBounds(cycle, this.videoDuration, this.width, this.margin);

		let fillColor: string;

		if (customColor) {
			fillColor = isHovered ? this.adjustColorOpacity(customColor, 0.8) : customColor;
		} else {
			const colors = this.theme.colors.identifiedCycle;
			fillColor = isHovered ? colors.hoverBackground : colors.background;
		}

		this.ctx.fillStyle = fillColor;

		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;
		this.ctx.fillRect(startX, barY, endX - startX, barHeight);

		this.ctx.strokeStyle = this.theme.colors.identifiedCycle.border;
		this.ctx.lineWidth = this.theme.dimensions.borderWidth;

		this.drawVerticalLine(startX, barY, barHeight);
		this.drawVerticalLine(endX, barY, barHeight);
	}

	private getOutOfRangeColors() {
		const result = this.theme.colors.outOfRangeCycle ?? OUT_OF_RANGE_CYCLE_COLORS;

		return result;
	}

	private getInRangeBackground(cycle: CycleData, isHighlighted: boolean): string {
		const colors = cycle.identified ? this.theme.colors.identifiedCycle : this.theme.colors.unidentifiedCycle;
		const result = isHighlighted ? colors.hoverBackground : colors.background;

		return result;
	}

	private getVideoEndSplitX(cycle: CycleData, videoEndTime?: number): number | null {
		if (!videoEndTime) {
			return null;
		}

		const isStraddlingVideoEnd = cycle.start < videoEndTime && videoEndTime < cycle.end;

		if (!isStraddlingVideoEnd) {
			return null;
		}

		const result = timeToX(videoEndTime, this.videoDuration, this.width, this.margin);

		return result;
	}

	public drawOutOfRangeArea(fromTime: number) {
		const startX = timeToX(fromTime, this.videoDuration, this.width, this.margin);
		const endX = timeToX(this.videoDuration, this.videoDuration, this.width, this.margin);
		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;
		const colors = this.getOutOfRangeColors();

		this.ctx.fillStyle = colors.background;
		this.ctx.fillRect(startX, barY, endX - startX, barHeight);
	}

	public drawOutOfRangeCycle(cycle: CycleData, isHighlighted = false, videoEndTime?: number) {
		const { startX, endX } = getCycleBounds(cycle, this.videoDuration, this.width, this.margin);

		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;
		const colors = this.getOutOfRangeColors();
		const splitX = this.getVideoEndSplitX(cycle, videoEndTime);
		const outOfRangeStartX = splitX ?? startX;

		this.ctx.save();

		if (splitX !== null) {
			this.ctx.fillStyle = this.getInRangeBackground(cycle, isHighlighted);
			this.ctx.fillRect(startX, barY, splitX - startX, barHeight);
		}

		this.ctx.fillStyle = isHighlighted ? colors.selectedBackground : colors.background;
		this.ctx.fillRect(outOfRangeStartX, barY, endX - outOfRangeStartX, barHeight);

		this.ctx.strokeStyle = colors.border;
		this.ctx.lineWidth = this.theme.dimensions.borderWidth;
		this.ctx.setLineDash(OUT_OF_RANGE_DASH);

		this.drawVerticalLine(startX, barY, barHeight);
		this.drawVerticalLine(endX, barY, barHeight);

		this.ctx.restore();

		if (!isHighlighted) {
			return;
		}

		this.ctx.fillStyle = colors.handles;

		this.drawMarker(startX, this.theme.dimensions.markerRadius, this.theme.dimensions.markerWidth);
		this.drawMarker(endX, this.theme.dimensions.markerRadius, this.theme.dimensions.markerWidth);
	}

	/**
	 * Draws all overlap regions for identified cycles with colors based on overlap count.
	 * @param cycles - All cycles to process (only identified ones are rendered).
	 */
	public drawOverlapRegions(cycles: CycleData[]) {
		const identifiedCycles = cycles.filter((c) => c.identified);

		if (identifiedCycles.length === 0) return;

		const boundaries = new Set<number>();

		identifiedCycles.forEach((cycle) => {
			boundaries.add(cycle.start);
			boundaries.add(cycle.end);
		});

		const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);

		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;

		for (let i = 0; i < sortedBoundaries.length - 1; i++) {
			const segmentStart = sortedBoundaries[i];
			const segmentEnd = sortedBoundaries[i + 1];
			const segmentMid = (segmentStart + segmentEnd) / 2;

			const overlapCount = this.countIdentifiedCyclesAtTime(segmentMid, identifiedCycles);

			if (overlapCount === 0) continue;

			const color = this.getOverlapColor(overlapCount);
			const segmentStartX = timeToX(segmentStart, this.videoDuration, this.width, this.margin);
			const segmentEndX = timeToX(segmentEnd, this.videoDuration, this.width, this.margin);

			this.ctx.fillStyle = color;
			this.ctx.fillRect(segmentStartX, barY, segmentEndX - segmentStartX, barHeight);
		}

		this.ctx.strokeStyle = this.theme.colors.identifiedCycle.border;
		this.ctx.lineWidth = this.theme.dimensions.borderWidth;

		identifiedCycles.forEach((cycle) => {
			const { startX, endX } = getCycleBounds(cycle, this.videoDuration, this.width, this.margin);
			this.drawVerticalLine(startX, barY, barHeight);
			this.drawVerticalLine(endX, barY, barHeight);
		});
	}

	/**
	 * Checks if two cycles overlap in time.
	 */
	private cyclesOverlap(cycleA: CycleData, cycleB: CycleData): boolean {
		if (cycleA.paired_cycle_id === cycleB.id || cycleB.paired_cycle_id === cycleA.id) {
			return false;
		}
		return cycleA.start < cycleB.end && cycleB.start < cycleA.end;
	}

	private hasSameTypeId(cycleA: CycleData, cycleB: CycleData): boolean {
		const typeIdA = cycleA.type_id;
		const typeIdB = cycleB.type_id;

		return Boolean(typeIdA && typeIdB && typeIdA === typeIdB);
	}

	private mergeIntervals(intervals: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
		if (intervals.length === 0) {
			return [];
		}

		const sorted = [...intervals].sort((a, b) => a.start - b.start);
		const merged: Array<{ start: number; end: number }> = [sorted[0]];

		for (let i = 1; i < sorted.length; i++) {
			const current = sorted[i];
			const last = merged[merged.length - 1];

			if (current.start <= last.end) {
				last.end = Math.max(last.end, current.end);
			} else {
				merged.push({ ...current });
			}
		}

		return merged;
	}

	/**
	 * Counts how many identified cycles overlap at a specific time point.
	 */
	private countIdentifiedCyclesAtTime(time: number, identifiedCycles: CycleData[]): number {
		let count = 0;

		identifiedCycles.forEach((cycle) => {
			if (time >= cycle.start && time < cycle.end) {
				count++;
			}
		});

		return count;
	}

	/**
	 * Returns the color based on overlap count.
	 * Uses configured overlapColors if available, otherwise defaults to theme colors.
	 */
	private getOverlapColor(overlapCount: number): string {
		if (!this.overlapColors) {
			return this.theme.colors.identifiedCycle.background;
		}

		if (this.overlapColors[overlapCount]) {
			return this.overlapColors[overlapCount];
		}

		const maxKey = Math.max(...Object.keys(this.overlapColors).map(Number));

		return this.overlapColors[maxKey];
	}

	/**
	 * Adjusts the opacity of a hex color.
	 * @param hexColor - Hex color code.
	 * @param opacity - Opacity value between 0 and 1.
	 * @returns RGBA color string.
	 */
	private adjustColorOpacity(hexColor: string, opacity: number): string {
		const hex = hexColor.replace('#', '');

		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	/**
	 * Draws a vertical line (cycle border).
	 * @param x - The horizontal position.
	 * @param y - The initial vertical position.
	 * @param height - The height of the line.
	 */
	private drawVerticalLine(x: number, y: number, height: number) {
		this.ctx.beginPath();
		this.ctx.moveTo(x, y);
		this.ctx.lineTo(x, y + height);
		this.ctx.stroke();
	}

	/**
	 * Draws the current cycle in editing mode with a blue style and interactive markers.
	 * @param cycle - The data of the current cycle being edited.
	 * @param isHovered - Whether the mouse is hovering over this cycle.
	 * @param showMarkers - Whether to draw the edit markers (needles). When false, only the blue band is drawn.
	 */
	public drawCurrentCycle(cycle: CycleData, isHovered = false, showMarkers = true) {
		const { startX, endX } = getCycleBounds(cycle, this.videoDuration, this.width, this.margin);

		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;

		const colors = this.theme.colors.unidentifiedCycle;
		let fillColor = colors.background;

		if (isHovered) {
			fillColor = colors.hoverBackground;
		}

		this.ctx.fillStyle = fillColor;
		this.ctx.fillRect(startX, barY, endX - startX, barHeight);

		if (!showMarkers) {
			return;
		}

		this.ctx.fillStyle = this.theme.colors.unidentifiedCycle.handles;

		this.drawMarker(startX, this.theme.dimensions.markerRadius, this.theme.dimensions.markerWidth);
		this.drawMarker(endX, this.theme.dimensions.markerRadius, this.theme.dimensions.markerWidth);
	}

	public drawTypeOverlapErrorOverlay(allCycles: CycleData[]) {
		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;
		const errorColor = this.theme.colors.error;

		if (!errorColor) return;

		const conflictIntervals: Array<{ start: number; end: number }> = [];

		for (let i = 0; i < allCycles.length; i++) {
			for (let j = i + 1; j < allCycles.length; j++) {
				const cycleA = allCycles[i];
				const cycleB = allCycles[j];

				if (!this.cyclesOverlap(cycleA, cycleB) || !this.hasSameTypeId(cycleA, cycleB)) {
					continue;
				}

				const overlapStart = Math.max(cycleA.start, cycleB.start);
				const overlapEnd = Math.min(cycleA.end, cycleB.end);

				if (overlapStart < overlapEnd) {
					conflictIntervals.push({ start: overlapStart, end: overlapEnd });
				}
			}
		}

		const mergedIntervals = this.mergeIntervals(conflictIntervals);

		mergedIntervals.forEach((interval) => {
			const segmentStartX = timeToX(interval.start, this.videoDuration, this.width, this.margin);
			const segmentEndX = timeToX(interval.end, this.videoDuration, this.width, this.margin);
			const segmentWidth = segmentEndX - segmentStartX;

			this.ctx.fillStyle = errorColor;
			this.ctx.fillRect(segmentStartX, barY, segmentWidth, barHeight);
		});
	}

	/**
	 * Draws editing handles on a selected identified cycle.
	 * When in edit mode, uses unidentified (blue) colors to indicate editing state.
	 * @param cycle - The data of the identified cycle being edited.
	 */
	public drawIdentifiedCycleWithHandles(cycle: CycleData) {
		this.drawCurrentCycle(cycle, false);
	}

	/**
	 * Draws an interactive marker (circle + rectangle) on the edges of the current cycle.
	 * @param x - The horizontal position of the marker.
	 * @param circleRadius - The radius of the top circle.
	 * @param rectWidth - The width of the bottom rectangle.
	 */
	private drawMarker(x: number, circleRadius: number, rectWidth: number) {
		const barY = this.height - this.theme.dimensions.cycleBarHeight;
		const circleY = barY - circleRadius - 2;

		this.ctx.beginPath();
		this.ctx.arc(x, circleY, circleRadius, CANVAS_GEOMETRY.ZERO_COORDINATE, CANVAS_GEOMETRY.FULL_CIRCLE_RADIANS);
		this.ctx.fill();

		const rectX = x - rectWidth / CANVAS_GEOMETRY.HALF_DIVISOR;
		const rectY = circleY + circleRadius;
		const rectHeight = this.theme.dimensions.cycleBarHeight + 2;
		this.ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
	}

	/**
	 * Returns a cached or newly created Image element from the needle SVG.
	 */
	private getNeedleSVGImage(color: string): HTMLImageElement | null {
		const cached = needleImageCache.get(color);
		if (cached && cached.complete) {
			return cached;
		}

		if (cached) {
			if (this.onImageLoad && !pendingCallbacks.has(this.onImageLoad)) {
				pendingCallbacks.add(this.onImageLoad);
			}
			return null;
		}

		const svgString = getTimeNeedleIconString(color);

		const encodedSvg = encodeURIComponent(svgString);
		const dataUri = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

		const img = new Image();
		img.src = dataUri;

		if (this.onImageLoad) {
			pendingCallbacks.add(this.onImageLoad);
		}

		img.onload = () => {
			needleImageCache.set(color, img);
			pendingCallbacks.forEach((cb) => cb());
			pendingCallbacks.clear();
		};

		if (img.complete) {
			needleImageCache.set(color, img);
			pendingCallbacks.clear();
			return img;
		}

		needleImageCache.set(color, img);
		return null;
	}

	/**
	 * Draws the current time indicator (needle) using SVG directly.
	 * @param currentTime - The current video time in seconds.
	 * @param currentCycle - The current cycle to adjust the needle's position.
	 * @param isScrubbing - Whether the user is actively scrubbing (dragging the needle).
	 */
	public drawCurrentTimeNeedle(currentTime: number, currentCycle: CycleData | null, isScrubbing = false) {
		const adjustedTime = CanvasDrawer.getAdjustedCurrentTime(currentTime, currentCycle);
		const progressX = timeToX(adjustedTime, this.videoDuration, this.width, this.margin);
		const needleColor = isScrubbing ? '#002FBB' : this.theme.colors.timeNeedle;
		const needleImage = this.getNeedleSVGImage(needleColor);

		if (!needleImage || !needleImage.complete || !this.isCanvas2DContext(this.ctx)) {
			this.drawCurrentTimeNeedleFallback(progressX, needleColor);

			return;
		}

		const svgWidth = 10;
		const svgHeight = 40;
		const drawX = progressX - svgWidth / 2;
		const drawY = 0;

		this.ctx.drawImage(needleImage, drawX, drawY, svgWidth, svgHeight);

		if (this.height > svgHeight) {
			this.ctx.fillStyle = needleColor;
			this.ctx.fillRect(progressX - 1, svgHeight, 2, this.height - svgHeight);
		}
	}

	/**
	 * Fallback: draws the needle manually when SVG is not available.
	 */
	private drawCurrentTimeNeedleFallback(progressX: number, color: string) {
		this.ctx.fillStyle = color;
		this.ctx.fillRect(progressX - 1, 0, 2, this.height);
	}

	/**
	 * Returns the current time without restrictions.
	 * The pointer can now move freely across the entire timeline.
	 * @param currentTime - The original video time.
	 * @param _currentCycle - The current cycle (unused, kept for compatibility).
	 * @returns The unchanged current time.
	 */
	private static getAdjustedCurrentTime(currentTime: number, _currentCycle: CycleData | null): number {
		return currentTime;
	}

	/**
	 * Draws a drag preview (ghost) of the cycle during drag operations
	 * Only shows when position is INVALID (red feedback)
	 * When valid, the actual cycle moves so no preview needed
	 * @param cycle - The cycle data for preview position
	 * @param isValid - Whether the current position is valid
	 */
	public drawDragPreview(cycle: CycleData, isValid: boolean) {
		if (isValid) return;

		const { startX, endX } = getCycleBounds(cycle, this.videoDuration, this.width, this.margin);

		const barHeight = this.theme.dimensions.cycleBarHeight;
		const barY = this.height - barHeight;

		this.ctx.save();

		this.ctx.fillStyle = 'rgba(209, 51, 66, 0.4)';
		this.ctx.fillRect(startX, barY, endX - startX, barHeight);

		this.ctx.strokeStyle = '#D13342';
		this.ctx.lineWidth = 2;
		this.ctx.setLineDash([5, 5]);

		this.ctx.beginPath();
		this.ctx.moveTo(startX, barY);
		this.ctx.lineTo(startX, barY + barHeight);
		this.ctx.stroke();
		this.ctx.beginPath();
		this.ctx.moveTo(endX, barY);
		this.ctx.lineTo(endX, barY + barHeight);
		this.ctx.stroke();

		this.ctx.restore();
	}
}
